import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Loader2 } from 'lucide-react'
import { PublicLayout } from '../../components/public/PublicLayout'
import { MultiStepForm } from '../../components/public/MultiStepForm'
import {
  getInfoCampaniaPublica,
  getFormularioPublico,
  enviarSolicitudEstandar,
  enviarPasoFormulario,
} from '../../api/publicoApi'
import type { CampaniaPublicaInfo, FormularioPublicoData } from '../../types'

export function SolicitudPublicaPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()

  useDocumentTitle('Solicitud de Muestra')

  const [info, setInfo] = useState<CampaniaPublicaInfo | null>(null)
  const [formulario, setFormulario] = useState<FormularioPublicoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uuid) return
    let cancelled = false
    const load = async () => {
      try {
        const infoData = await getInfoCampaniaPublica(uuid)
        if (cancelled) return
        setInfo(infoData)
        const formData = await getFormularioPublico(uuid)
        if (cancelled) return
        setFormulario(formData)
      } catch (err: unknown) {
        if (cancelled) return
        const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        if (status === 410) {
          if (msg?.includes('ya') || msg?.includes('solicitud')) {
            navigate(`/muestras-medicas/ya-solicitada`, { replace: true })
          } else {
            navigate(`/muestras-medicas/no-disponible?mensaje=${encodeURIComponent(msg || '')}`, { replace: true })
          }
          return
        }
        setError(msg || 'No se pudo cargar el formulario')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [uuid, navigate])

  if (loading) {
    return (
      <PublicLayout>
        <div className="space-y-4">
          <Skeleton className="h-12 w-64 mx-auto" />
          <Skeleton className="h-96 w-full" />
        </div>
      </PublicLayout>
    )
  }

  if (error || !info || !formulario) {
    return (
      <PublicLayout>
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-6 text-center">
          <p className="text-gray-600">{error || 'Formulario no disponible'}</p>
        </div>
      </PublicLayout>
    )
  }

  if (formulario.tipo === 'personalizado' && formulario.pasos) {
    return (
      <PublicLayout>
        <MultiStepForm
          pasos={formulario.pasos}
          mensajeBienvenida={info.mensaje_bienvenida}
          tiempoEstimado={info.tiempo_estimado}
          imagenUrl={info.imagen_url}
          titulo={info.titulo}
          descripcion={info.descripcion}
          onSubmit={async (respuestas) => {
            await enviarPasoFormulario(uuid!, {
              paso_numero: formulario.pasos!.length,
              respuestas,
            })
            const redirect = info.descripcion ? `&redirect=${encodeURIComponent(info.descripcion)}` : ''
            navigate(`/muestras-medicas/gracias/${uuid}?${redirect}`, { replace: true })
          }}
        />
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <FormularioEstandar
        info={info}
        provincias={formulario.provincias ?? []}
        uuid={uuid!}
      />
    </PublicLayout>
  )
}

// --- Formulario Estándar ---

function validateEmail(email: string): boolean {
  if (!email || email.length === 0) return true
  const atCount = (email.match(/@/g) || []).length
  if (atCount !== 1) return false
  const parts = email.split('@')
  if (parts.length !== 2) return false
  const [localPart, domainPart] = parts
  if (!localPart || !domainPart) return false
  if (!domainPart.includes('.')) return false
  const lastDotIndex = domainPart.lastIndexOf('.')
  if (lastDotIndex === -1 || lastDotIndex === domainPart.length - 1) return false
  return true
}

function formatTelefonoEcuador(value: string): string {
  let clean = value.replace(/[^0-9+]/g, '')
  if (!clean.startsWith('+')) {
    if (clean.startsWith('0')) clean = clean.substring(1)
    if (clean.startsWith('593')) {
      clean = '+' + clean
    } else if (clean.length > 0) {
      clean = '+593' + clean
    }
  }
  if (clean.length > 13) clean = clean.substring(0, 13)
  return clean
}

function validateTelefono(value: string): boolean {
  if (!value || value.length === 0) return true
  const numbersOnly = value.replace(/[^0-9]/g, '')
  const isValidFormat = value.startsWith('+593') && numbersOnly.length === 12
  const phoneNumber = numbersOnly.substring(3)
  return isValidFormat && phoneNumber.startsWith('9')
}

interface FormularioEstandarProps {
  info: CampaniaPublicaInfo
  provincias: string[]
  uuid: string
}

function FormularioEstandar({ info, provincias, uuid }: FormularioEstandarProps) {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    telefono: '',
    correo: '',
    provincia: '',
    ciudad: '',
    aceptar_terminos: false,
    autorizar_envio_mm: false,
  })
  const [ciudades] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [telError, setTelError] = useState('')
  const [emailError, setEmailError] = useState('')

  const inputClass = "w-full px-4 py-3 border border-gray-300 rounded-lg text-base transition-all duration-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-600"

  const validate = (): string[] => {
    const errs: string[] = []
    if (!form.nombre.trim()) errs.push('Nombre es requerido')
    if (!form.apellido.trim()) errs.push('Apellido es requerido')
    if (!form.telefono.trim()) errs.push('Teléfono es requerido')
    else if (!validateTelefono(form.telefono)) {
      errs.push('Teléfono inválido (formato: +593 9XX XXX XXXX)')
    }
    if (!form.correo.trim()) errs.push('Correo es requerido')
    else if (!validateEmail(form.correo)) {
      errs.push('Correo inválido')
    }
    if (!form.provincia) errs.push('Selecciona una provincia')
    if (!form.aceptar_terminos) errs.push('Debes aceptar los términos')
    if (!form.autorizar_envio_mm) errs.push('Debes autorizar el envío')
    return errs
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validate()
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    setIsSubmitting(true)
    setErrors([])
    try {
      let telefono = form.telefono.replace(/\s/g, '')
      if (telefono.startsWith('0')) telefono = `+593${telefono.slice(1)}`
      if (!telefono.startsWith('+593')) telefono = `+593${telefono}`

      await enviarSolicitudEstandar(uuid, { ...form, telefono })
      navigate(`/muestras-medicas/gracias/${uuid}`, { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setErrors([msg || 'Error al enviar la solicitud'])
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="bg-white rounded-lg p-3 sm:p-4 inline-block shadow-sm mb-4 sm:mb-6">
          <img src="/logo-tonal-negro.png" alt="Tonal" className="h-10 sm:h-12 w-auto" onError={(e) => { e.currentTarget.style.display = 'none' }} />
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center flex-wrap">
          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          {info.titulo}
        </h1>
        {info.descripcion && (
          <p className="text-sm sm:text-base text-gray-600 max-w-lg mx-auto px-2">{info.descripcion}</p>
        )}
      </div>

      {/* Imagen de la campaña */}
      {info.imagen_url && (
        <div className="mb-6 sm:mb-8 flex justify-center">
          <img
            src={info.imagen_url}
            alt={info.titulo}
            className="max-w-32 w-auto h-20 sm:max-w-40 sm:h-24 lg:max-w-sm lg:h-32 object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        </div>
      )}

      {/* Formulario */}
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-7">
            {/* Datos Personales */}
            <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Datos Personales
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Nombre</span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Tu nombre"
                    value={form.nombre}
                    onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                    className={inputClass}
                  />
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Apellido</span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Tu apellido"
                    value={form.apellido}
                    onChange={(e) => setForm((f) => ({ ...f, apellido: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Datos de Contacto */}
            <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Datos de Contacto
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>Teléfono</span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="+593 123456789"
                    value={form.telefono}
                    onChange={(e) => {
                      const formatted = formatTelefonoEcuador(e.target.value)
                      setForm((f) => ({ ...f, telefono: formatted }))
                      if (validateTelefono(formatted)) setTelError('')
                    }}
                    onBlur={() => {
                      if (form.telefono.length > 0 && !validateTelefono(form.telefono)) {
                        setTelError('Debe seguir el formato: +593991234567')
                      } else {
                        setTelError('')
                      }
                    }}
                    className={`${inputClass} ${telError ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                  />
                  <p className="mt-1 text-xs text-gray-500">Ejemplo: 0998765432</p>
                  {telError && <p className="mt-1 text-sm text-red-600">{telError}</p>}
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <span>Correo Electrónico</span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={form.correo}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, correo: e.target.value }))
                      if (validateEmail(e.target.value.trim())) setEmailError('')
                    }}
                    onBlur={() => {
                      const val = form.correo.trim()
                      if (val.length > 0 && !validateEmail(val)) {
                        setEmailError('Debe seguir el formato: correo@mail.com')
                      } else {
                        setEmailError('')
                      }
                    }}
                    className={`${inputClass} ${emailError ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                  />
                  {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
                </div>
              </div>
            </div>

            {/* Ubicación */}
            <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Ubicación
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Provincia</span>
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <Select value={form.provincia} onValueChange={(val) => setForm((f) => ({ ...f, provincia: val, ciudad: '' }))}>
                    <SelectTrigger className="w-full px-4 py-3 h-auto border border-gray-300 rounded-lg">
                      <SelectValue placeholder="Selecciona provincia" />
                    </SelectTrigger>
                    <SelectContent>
                      {provincias.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>Ciudad</span>
                  </label>
                  {ciudades.length > 0 ? (
                    <Select value={form.ciudad} onValueChange={(val) => setForm((f) => ({ ...f, ciudad: val }))}>
                      <SelectTrigger className="w-full px-4 py-3 h-auto border border-gray-300 rounded-lg">
                        <SelectValue placeholder="Selecciona ciudad" />
                      </SelectTrigger>
                      <SelectContent>
                        {ciudades.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <input
                      type="text"
                      placeholder="Tu ciudad"
                      value={form.ciudad}
                      onChange={(e) => setForm((f) => ({ ...f, ciudad: e.target.value }))}
                      className={inputClass}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Términos y Autorizaciones */}
            <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Términos y Autorizaciones
              </h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label htmlFor="aceptar_terminos" className="flex items-start space-x-3 cursor-pointer">
                    <div className="flex items-center h-5 mt-0.5">
                      <input
                        type="checkbox"
                        id="aceptar_terminos"
                        checked={form.aceptar_terminos}
                        onChange={(e) => setForm((f) => ({ ...f, aceptar_terminos: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-300 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900 flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Acepto los términos y condiciones</span>
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                      <p className="text-sm text-blue-700 mt-1">
                        Al marcar esta casilla, acepto los términos y condiciones para el procesamiento de mis datos personales.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                  <label htmlFor="autorizar_envio" className="flex items-start space-x-3 cursor-pointer">
                    <div className="flex items-center h-5 mt-0.5">
                      <input
                        type="checkbox"
                        id="autorizar_envio"
                        checked={form.autorizar_envio_mm}
                        onChange={(e) => setForm((f) => ({ ...f, autorizar_envio_mm: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-300 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900 flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <span>Autorizo el envío de muestras médicas</span>
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                      <p className="text-sm text-blue-700 mt-1">
                        Autorizo a Tonal Strategies a enviarme muestras médicas a la dirección proporcionada.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {errors.length > 0 && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md space-y-1 flex items-start">
                <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  {errors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Botón de Envío */}
            <div className="pt-4 sm:pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center items-center px-6 sm:px-8 py-3 sm:py-4 border border-transparent rounded-lg text-base sm:text-lg font-semibold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
                {isSubmitting ? 'Enviando solicitud...' : 'Solicitar Muestras Médicas'}
              </button>
            </div>

            {/* Información Adicional */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 mt-4 sm:mt-6">
              <div className="text-center text-sm text-gray-600">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-4 h-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Los campos marcados con * son obligatorios.</span>
                </div>
                <p>Sus datos serán tratados de forma confidencial conforme a la ley de protección de datos.</p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
